import React from "react";
import { Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import { Check, ChevronDown, Building2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useLang } from "../lib/LangContext";
import { supportedLanguages, waLink } from "../lib/mock";
import { NAV } from "../constants/testIds";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const current = supportedLanguages.find((l) => l.code === lang) || supportedLanguages[0];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-lokam-cream/80 border-b border-black/5">
      <div className="px-6 md:px-12 lg:px-20 max-w-[1400px] mx-auto flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a
          href="#top"
          data-testid={NAV.logo}
          className="flex items-center gap-2"
          aria-label="Lokam Health"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_medicine-dial/artifacts/897ara6e_Primary%20-%20Logo.svg"
            alt="Lokam"
            className="h-9 md:h-10 w-auto"
          />
        </a>

        {/* Center nav */}
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-lokam-ink-soft">
          <a
            data-testid={NAV.linkHow}
            href="/#how-it-works"
            className="hover:text-lokam-forest transition-colors"
          >
            {t.nav.how}
          </a>
          <a
            data-testid={NAV.linkCoverage}
            href="/#coverage"
            className="hover:text-lokam-forest transition-colors"
          >
            {t.nav.coverage}
          </a>
          <a
            data-testid={NAV.linkSupport}
            href="/#support"
            className="hover:text-lokam-forest transition-colors"
          >
            {t.nav.support}
          </a>
          <Link
            to="/hospitals"
            data-testid="nav-link-hospitals"
            className="group inline-flex items-center gap-1.5 rounded-full border border-lokam-tangerine/40 bg-lokam-tangerine/5 hover:bg-lokam-tangerine hover:text-white px-3 py-1.5 text-xs font-bold text-lokam-tangerine uppercase tracking-[0.08em] transition-colors"
          >
            <Building2 className="h-3.5 w-3.5" />
            {t.hospitals_link || "For Hospitals"}
          </Link>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Mobile-only For Hospitals pill */}
          <Link
            to="/hospitals"
            data-testid="nav-link-hospitals-mobile"
            className="md:hidden inline-flex items-center gap-1 rounded-full border border-lokam-tangerine/40 bg-lokam-tangerine/5 px-2.5 py-1.5 text-[10px] font-bold text-lokam-tangerine uppercase tracking-wider"
          >
            <Building2 className="h-3 w-3" />
            {t.hospitals_link || "Hospitals"}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid={NAV.langTrigger}
              className="inline-flex items-center gap-1.5 rounded-full border border-lokam-border bg-white/70 hover:bg-white px-3 py-2 text-xs md:text-sm font-semibold text-lokam-ink transition"
            >
              <span className="text-lokam-tangerine">{current.short}</span>
              <span className="hidden sm:inline text-lokam-ink-soft">{current.label}</span>
              <ChevronDown className="h-3.5 w-3.5 text-lokam-ink-soft" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              {supportedLanguages.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={NAV.langOption(l.code)}
                  onClick={() => setLang(l.code)}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <span className="font-medium">{l.label}</span>
                  {lang === l.code && <Check className="h-4 w-4 text-lokam-tangerine" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={NAV.ctaWhatsapp}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-lokam-forest hover:bg-lokam-forest-soft text-lokam-cream px-4 py-2 text-sm font-semibold transition-colors"
          >
            <FaWhatsapp className="h-4 w-4 text-lokam-whatsapp" />
            <span>{t.nav.cta}</span>
          </a>
        </div>
      </div>
    </header>
  );
};

export default Header;
